import React from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Button } from '@pandiafi/design-system'
import { useLocation, useNavigate } from 'react-router-dom'

import { ResidentAppError } from '../../common/error'
import { ResidentAppErrorCode } from '../../common/types'
import { ErrorPageNavbar, Footer, Error } from '../components'
import { resolveResidentAppErrorMessage } from '../utils/resolveResidentAppErrorMessage'

import { ErrorPage } from 'views/ErrorPage'
import routePath from 'router/routePath'

interface ErrorHandlerProps {
  children: React.ReactNode
}

export const ErrorHandler: React.FC<ErrorHandlerProps> = ({ children }) => {
  const { t } = useTranslation(['errors', 'common'])
  const navigate = useNavigate()
  const location = useLocation()

  const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    const resolved = resolveResidentAppErrorMessage(error as Error, t)
    const isImpersonationWriteAccess =
      error instanceof ResidentAppError &&
      error.errorCode === ResidentAppErrorCode.IMPERSONATION_WRITE_ACCESS_DENIED

    const actionOverride = isImpersonationWriteAccess
      ? {
          onClick: () => navigate(-1),
          buttonText: t('common:back'),
        }
      : undefined

    return (
      <>
        <ErrorPageNavbar />
        <ErrorPage
          message={resolved.message}
          illustration={resolved.illustration}
          eventId={resolved.eventId}
          action={
            location.pathname !== routePath.root && (
              <Button
                onClick={() => {
                  resetErrorBoundary()
                  if (actionOverride?.onClick) {
                    actionOverride.onClick()
                  } else if (resolved.reloadButtonText) {
                    window.location.reload()
                  } else {
                    navigate(routePath.root)
                  }
                }}
              >
                {actionOverride?.buttonText ??
                  resolved.reloadButtonText ??
                  t('other.backToLogin')}
              </Button>
            )
          }
        />
        <Footer layout="public" />
      </>
    )
  }

  return (
    <ErrorBoundary fallbackRender={fallbackRender}>{children}</ErrorBoundary>
  )
}
